import React, { useEffect, useRef, useState, useCallback, Fragment, useMemo } from "react";
import PrescriptionForm from "./PrescriptionForm";
import MedicationIcon from "@mui/icons-material/Medication";
import PrintIcon from "@mui/icons-material/Print";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SaveIcon from "@mui/icons-material/Save";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import {
  setPresPrintaction,
  setPresPrintclinic,
  setPresPrintPatient,
  setPresPrintTreatment,
} from "../../../redux/printstate/printActions";
import baseUrl from "../../../api/baseUrl";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import PrePrint from "../../setting/manageprint/preprint/PrePrint";
import { useParams } from "react-router-dom";
import {
  setloadeActive,
  setloadeDeactive,
} from "../../../redux/loader/loaderActions";
import {
  addAditionalNote,
  addDiagnoseNote,
  addDiagnoses,
  addDiagnosesImage,
  addInvestigations,
  addPatientComplains,
  addPrescriptions,
  addPrescription,
  addTreatmentImage,
  removePrescription,
} from "../../../redux/diagnosestate/diagnoseAction";
import { toast } from "react-toastify";
import {
  validatePrescriptionForm,
  type PrescriptionFormData,
} from "./prescriptionFormUtils";

const createDraftId = () =>
  `draft-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const toDateKey = (value?: string) => {
  if (!value) return "Unknown";
  const key = String(value).split("T")[0];
  return key || "Unknown";
};

interface PastPrescriptionItem {
  _id: string;
  treatmentId: string;
  visitDate: string;
  doctorName: string;
  drugs: string;
  type: string;
  dose: string;
  frequency: string;
  duration: string;
  createdAt: string;
}

interface DateWiseRow {
  _id: string;
  drugsId?: string;
  drugs: string;
  type: string;
  dose: string;
  frequency: string;
  duration: string;
  doctorName?: string;
  source: "current" | "past";
  treatmentId?: string;
  createdAt?: string;
}

interface DateWiseGroup {
  date: string;
  categories: { category: string; items: DateWiseRow[] }[];
}

const flattenDateWiseItems = (group: DateWiseGroup): DateWiseRow[] =>
  group.categories.flatMap((category) => category.items);

/** Unchecked: empty. Checked: teal fill + white checkmark (same as buttons). */
const PRESCRIPTION_CHECKBOX_CLASS =
  "appearance-none w-3.5 h-3.5 shrink-0 rounded border-2 border-slate-300 dark:border-slate-500 bg-white dark:bg-slate-800 cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:ring-offset-0 checked:border-teal-500 checked:bg-teal-500 checked:bg-center checked:bg-no-repeat checked:bg-[length:12px_12px] checked:[background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M3.5 8.5l3 3 6-6'/%3E%3C/svg%3E\")]";

const toEditItem = (item: DateWiseRow) => ({
  _id: item._id,
  drugsId: item.drugsId,
  drugs: item.drugs,
  type: item.type,
  dose: item.dose,
  frequency: item.frequency,
  duration: item.duration,
  createdAt: item.createdAt,
});

function Prescription() {
  const dispatch = useDispatch();
  const { ID } = useParams<{ ID: string }>();
  const prescription = useSelector(
    (state: RootState) => state.diagnose.prescription
  );
  const printRef = useRef<HTMLDivElement>(null);
  const [printData, setPrintData] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [editItem, setEditItem] = useState<any>(null);
  const [draftFormIds, setDraftFormIds] = useState<string[]>([]);
  const [isSavingDrafts, setIsSavingDrafts] = useState(false);
  const draftFormDataRef = useRef<Record<string, PrescriptionFormData>>({});
  const [pastPrescriptions, setPastPrescriptions] = useState<PastPrescriptionItem[]>([]);
  const [showDateWise, setShowDateWise] = useState(false);

  const dateWiseGroups = useMemo<DateWiseGroup[]>(() => {
    const byDate = new Map<string, DateWiseRow[]>();

    const pushRow = (date: string, row: DateWiseRow) => {
      const list = byDate.get(date) ?? [];
      list.push(row);
      byDate.set(date, list);
    };

    (prescription ?? []).forEach((item: any) => {
      pushRow(toDateKey(item?.createdAt), {
        _id: item?._id,
        drugsId: item?.drugsId || "",
        drugs: item?.drugs || "",
        type: item?.type || "",
        dose: item?.dose || "",
        frequency: item?.frequency || "",
        duration: item?.duration || "",
        source: "current",
        treatmentId: ID,
        createdAt: item?.createdAt || "",
      });
    });

    pastPrescriptions.forEach((item) => {
      pushRow(toDateKey(item.visitDate || item.createdAt), {
        _id: item._id,
        drugs: item.drugs || "",
        type: item.type || "",
        dose: item.dose || "",
        frequency: item.frequency || "",
        duration: item.duration || "",
        doctorName: item.doctorName,
        source: "past",
        treatmentId: item.treatmentId,
        createdAt: item.createdAt || item.visitDate || "",
      });
    });

    return Array.from(byDate.entries())
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([date, items]) => {
        const byCategory = new Map<string, DateWiseRow[]>();
        items.forEach((row) => {
          const category = (row.type || "Other").trim() || "Other";
          const list = byCategory.get(category) ?? [];
          list.push(row);
          byCategory.set(category, list);
        });
        const categories = Array.from(byCategory.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([category, categoryItems]) => ({
            category,
            items: categoryItems,
          }));
        return { date, categories };
      });
  }, [ID, prescription, pastPrescriptions]);

  const getData = useCallback(() => {
    if (!ID) return;

    dispatch(setloadeActive());
    baseUrl
      .get("treatment/find-treatment", {
        headers: { treatmentid: ID },
      })
      .then((response) => {
        const {
          aditionalNote,
          patientComplain,
          diagnose,
          imageDiagnose,
          imageTreatment,
          complain,
          investigation,
          prescription,
        } = response.data.data;

        function cleanDoseArray(data: any) {
          return data.map((item: any) => ({
            _id: item?._id || "",
            dose: item?.doseId || "",
            drugsId: item?.drugsId?._id || "",
            drugs: item?.drugsId?.drugs || "",
            type: item?.drugsId?.type || "",
            duration: item?.duration || "",
            frequency: item?.frequency || "",
            createdAt: item?.createdAt || "",
          }));
        }

        dispatch(addAditionalNote(aditionalNote ?? ""));
        dispatch(addDiagnoseNote(patientComplain ?? ""));
        dispatch(addDiagnoses(diagnose ?? []));
        dispatch(addDiagnosesImage(imageDiagnose ?? []));
        dispatch(addTreatmentImage(imageTreatment ?? []));
        dispatch(addPatientComplains(complain ?? []));
        dispatch(addInvestigations(investigation ?? []));
        dispatch(addPrescriptions(cleanDoseArray(prescription) ?? []));
      })
      .catch(console.error)
      .finally(() => dispatch(setloadeDeactive()));
  }, [ID, dispatch]);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    if (!ID) return;

    baseUrl
      .get("treatment/findPastPrescriptionByTreatmentId", {
        headers: { treatmentid: ID },
      })
      .then((response) => {
        setPastPrescriptions(response.data.data ?? []);
      })
      .catch(console.error);
  }, [ID]);

  useEffect(() => {
    if (printData) {
      handleDownloadPdf();
    }
  }, [printData]);

  const dispatchPrintActions = useCallback(
    (e: any) => {
      e?.other?.clinicId?.prescriptionBill?.forEach((element: any) => {
        const { title, top, left, show, size, width, height } = element;
        if (top)
          dispatch(
            setPresPrintaction({ name: title, target: "top", value: top })
          );
        if (left)
          dispatch(
            setPresPrintaction({ name: title, target: "left", value: left })
          );
        if (show !== undefined)
          dispatch(
            setPresPrintaction({ name: title, target: "show", value: show })
          );
        if (size)
          dispatch(
            setPresPrintaction({ name: title, target: "size", value: size })
          );
        if (width)
          dispatch(
            setPresPrintaction({ name: title, target: "width", value: width })
          );
        if (height)
          dispatch(
            setPresPrintaction({ name: title, target: "height", value: height })
          );
      });
      dispatch(
        setPresPrintclinic({
          clinicName: e?.other?.clinicId?.title,
          tpNo: e?.other?.clinicId?.tpNo,
          address: e?.other?.clinicId?.address,
          drName: e?.other?.doctorId?.firstName,
          printImages: e?.other?.clinicId?.printImages,
          headerImages: e?.other?.clinicId?.headerImages,
          footerImages: e?.other?.clinicId?.footerImages,
        })
      );
      dispatch(
        setPresPrintPatient({
          invoiceNo: e?.other?.invoiceNo,
          regNo: e?.patientData?.regNo,
          fullName: e?.patientData?.fullName,
          mobileNo: e?.patientData?.mobileNo,
          gender: e?.patientData?.gender,
          dob: e?.patientData?.dob,
        })
      );

      function cleanDoseArray(data: any) {
        return data.map((item: any) => ({
          _id: item?._id || "",
          dose: item?.doseId || "",
          drugsId: item?.drugsId?._id || "",
          drugs: item?.drugsId?.drugs || "",
          type: item?.drugsId?.type || "",
          duration: item?.duration || "",
          frequency: item?.frequency || "",
          createdAt: item?.createdAt || "",
        }));
      }

      dispatch(setPresPrintTreatment(cleanDoseArray(e?.prescription)));
    },
    [dispatch]
  );
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;

    setSelectedIds((prev) => {
      if (checked) {
        return [...prev, name];
      } else {
        return prev.filter((id) => id !== name);
      }
    });
  };
  const handlePrint = useCallback(
    async (e: any) => {
      try {
        if (selectedIds.length === 0) {
          toast.error("select prescription.");
          return;
        }
        const response = await baseUrl.get(
          "/client/treatments/prescription/getPrescriptionById",
          {
            headers: { treatmentid: e, prescrip: selectedIds },
          }
        );

        setPrintData(response.data);
        dispatchPrintActions(response.data);
      } catch (error) {
        console.log(error);
      }
    },
    [selectedIds, dispatchPrintActions]
  );

  const printDateWiseItems = useCallback(
    async (items: DateWiseRow[], dateLabel?: string) => {
      if (items.length === 0) {
        toast.error("No prescriptions to print.");
        return;
      }

      const currentIds = items
        .filter((item) => item.source === "current" && item._id)
        .map((item) => item._id);
      const layoutTreatmentId =
        (currentIds.length > 0 && ID) ||
        items.find((item) => item.treatmentId)?.treatmentId ||
        ID;
      const layoutIds =
        currentIds.length > 0
          ? currentIds
          : items.filter((item) => item._id).map((item) => item._id);

      if (!layoutTreatmentId || layoutIds.length === 0) {
        toast.error("Unable to print prescriptions.");
        return;
      }

      try {
        dispatch(setloadeActive());
        const response = await baseUrl.get(
          "/client/treatments/prescription/getPrescriptionById",
          {
            headers: {
              treatmentid: layoutTreatmentId,
              prescrip: layoutIds,
            },
          }
        );

        dispatchPrintActions(response.data);
        dispatch(
          setPresPrintTreatment(
            items.map((item) => ({
              _id: item._id,
              drugs: item.drugs,
              type: item.type,
              dose: item.dose,
              frequency: item.frequency,
              duration: item.duration,
              createdAt: dateLabel || toDateKey(item.createdAt),
            }))
          )
        );
        setPrintData(response.data);
      } catch (error) {
        console.error(error);
        toast.error("Failed to print prescriptions.");
      } finally {
        dispatch(setloadeDeactive());
      }
    },
    [ID, dispatch, dispatchPrintActions]
  );

  const handlePrintDateWise = useCallback(
    async (group: DateWiseGroup) => {
      const allItems = flattenDateWiseItems(group);
      const selectedInDate = allItems.filter((item) =>
        selectedIds.includes(item._id)
      );
      if (selectedInDate.length === 0) {
        toast.error("select prescription.");
        return;
      }
      await printDateWiseItems(selectedInDate, group.date);
    },
    [printDateWiseItems, selectedIds]
  );

  const toggleDateGroupSelection = useCallback(
    (group: DateWiseGroup, checked: boolean) => {
      const ids = flattenDateWiseItems(group).map((item) => item._id);
      setSelectedIds((prev) => {
        if (checked) {
          return Array.from(new Set([...prev, ...ids]));
        }
        return prev.filter((id) => !ids.includes(id));
      });
    },
    []
  );

  const remove = useCallback(
    async (e: any) => {
      try {
        await baseUrl.patch(
          "/client/treatments/prescription/removePrescription",
          {},
          {
            headers: { treatmentid: ID, prescriptionid: e },
          }
        );
        console.log({ treatmentid: ID, prescriptionid: e });
        dispatch(removePrescription({ hasId: true, _id: e }));
        setSelectedIds((prev) => prev.filter((id) => id !== e));
      } catch (error) {
        console.log(error);
      }
    },
    [ID, dispatch]
  );

  const handleDraftChange = useCallback(
    (draftId: string, formData: PrescriptionFormData) => {
      draftFormDataRef.current[draftId] = formData;
    },
    []
  );

  const handleDraftRemove = useCallback((draftId: string) => {
    delete draftFormDataRef.current[draftId];
    setDraftFormIds((prev) => prev.filter((id) => id !== draftId));
  }, []);

  const addDraftForm = useCallback(() => {
    setEditItem(null);
    setDraftFormIds((prev) => [...prev, createDraftId()]);
  }, []);

  const saveAllPendingPrescriptions = useCallback(async () => {
    if (!ID || draftFormIds.length === 0) return;

    setIsSavingDrafts(true);
    dispatch(setloadeActive());
    try {
      for (let index = 0; index < draftFormIds.length; index++) {
        const draftId = draftFormIds[index];
        const formData = draftFormDataRef.current[draftId];
        if (!formData) {
          toast.error(`Prescription row ${index + 1}: form data is missing`);
          return;
        }

        const validation = validatePrescriptionForm(formData);
        if (!validation.valid) {
          toast.error(`Prescription row ${index + 1}: ${validation.message}`);
          return;
        }

        const response = await baseUrl.patch(
          "/client/treatments/prescription/addprescription",
          validation.payload,
          {
            headers: { treatmentid: ID },
          }
        );

        const normalizedItem = {
          ...validation.payload,
          createdAt: response.data.data.createdAt,
          _id: response.data.data._id,
          drugs: validation.payload.drugs,
          dose: validation.payload.doseId,
        };
        dispatch(addPrescription(normalizedItem));
      }

      const savedCount = draftFormIds.length;
      draftFormDataRef.current = {};
      setDraftFormIds([]);
      toast.success(
        savedCount === 1
          ? "Prescription saved"
          : `${savedCount} prescriptions saved`
      );
    } catch (error: any) {
      const message =
        error?.response?.data?.message || "Failed to save prescriptions";
      toast.error(message);
      console.error(error);
    } finally {
      setIsSavingDrafts(false);
      dispatch(setloadeDeactive());
    }
  }, [ID, draftFormIds, dispatch]);

  const handleEdit = useCallback((item: any) => {
    setDraftFormIds([]);
    draftFormDataRef.current = {};
    setEditItem(item);
  }, []);

  const handleDownloadPdf = async () => {
    if (printRef.current) {
      printRef.current.style.display = "block";
      const element = printRef.current;
      const options = {
        scale: 2, // Improve quality
        useCORS: true, // Allow cross-origin images
        allowTaint: true,
      };

      const canvas = await html2canvas(element, options);
      const imgData = canvas.toDataURL("image/png");

      const imgWidth = 210; // A4 width in mm
      const imgProps = canvas.width / canvas.height;
      const imgHeight = imgWidth / imgProps; // Auto height based on aspect ratio

      const pdf = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: [imgWidth, imgHeight], // Dynamic height
      });

      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight, "", "FAST");

      const pdfBlob = pdf.output("blob");
      const blobUrl = URL.createObjectURL(pdfBlob);

      // Create an iframe and append to document
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.width = "0";
      iframe.style.top = "0";
      iframe.style.height = "0";
      iframe.style.border = "none";
      printRef.current.style.transform = "none";
      document.body.appendChild(iframe);
      iframe.src = blobUrl;

      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
          printRef.current!.style.display = "none";
        }, 500);
      };
    } else {
      console.error("Content ref is not defined");
    }
  };
  return (
    <div className="space-y-3 ">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 p-2.5 bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 bg-teal-100 dark:bg-teal-900/40 rounded-lg flex items-center justify-center shrink-0">
            <MedicationIcon className="text-teal-600 dark:text-teal-400" sx={{ fontSize: 16 }} />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">Prescription</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {prescription?.length || 0} drugs prescribed
              {draftFormIds.length > 0 && (
                <span className="ml-1 text-amber-600 dark:text-amber-400">
                  ({draftFormIds.length} unsaved)
                </span>
              )}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            setShowDateWise((prev) => {
              const next = !prev;
              if (next) {
                setSelectedIds(
                  dateWiseGroups
                    .flatMap(flattenDateWiseItems)
                    .map((item) => item._id)
                    .filter(Boolean)
                );
              }
              return next;
            });
          }}
          className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold transition-all shrink-0 ${
            showDateWise
              ? "bg-teal-600 text-white shadow-md"
              : "bg-teal-500 text-white hover:bg-teal-600 shadow-sm"
          }`}
          aria-pressed={showDateWise}
          title="Show prescriptions by date and category"
        >
          <CalendarMonthIcon sx={{ fontSize: 16 }} />
          {showDateWise ? "Hide Date-wise" : "Date-wise View"}
        </button>
      </div>

      {/* Date-wise categorized prescriptions */}
      {showDateWise && (
        <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
            <CalendarMonthIcon className="text-teal-600 dark:text-teal-400" sx={{ fontSize: 16 }} />
            <div>
              <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                Date-wise Prescriptions
              </h4>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Grouped by date and drug category
                {selectedIds.length > 0 && (
                  <span className="ml-1 text-teal-600 dark:text-teal-400">
                    · {selectedIds.length} selected
                  </span>
                )}
              </p>
            </div>
          </div>

          {dateWiseGroups.length === 0 ? (
            <div className="py-8 text-center text-slate-400 dark:text-slate-500">
              <CalendarMonthIcon sx={{ fontSize: 36 }} className="mb-2" />
              <p className="text-xs">No prescriptions to show by date</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-700">
              {dateWiseGroups.map((group) => {
                const groupItems = flattenDateWiseItems(group);
                const groupIds = groupItems.map((item) => item._id);
                const selectedInGroup = groupIds.filter((id) =>
                  selectedIds.includes(id)
                );
                const allGroupSelected =
                  groupIds.length > 0 && selectedInGroup.length === groupIds.length;

                return (
                <div key={group.date} className="p-3 space-y-2.5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <label className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold cursor-pointer">
                        <input
                          type="checkbox"
                          className={PRESCRIPTION_CHECKBOX_CLASS}
                          checked={allGroupSelected}
                          onChange={(e) =>
                            toggleDateGroupSelection(group, e.target.checked)
                          }
                        />
                        <CalendarMonthIcon sx={{ fontSize: 14 }} />
                        {group.date}
                      </label>
                      {selectedInGroup.length > 0 && (
                        <span className="text-[10px] text-teal-600 dark:text-teal-400 font-medium">
                          {selectedInGroup.length} selected
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handlePrintDateWise(group)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-teal-500 to-emerald-600 text-white font-medium text-xs hover:shadow-lg hover:shadow-teal-200 dark:hover:shadow-teal-900/30 transition-all"
                      title={`Print selected prescriptions for ${group.date}`}
                    >
                      <PrintIcon sx={{ fontSize: 14 }} />
                      {selectedInGroup.length > 0
                        ? `Print Selected (${selectedInGroup.length})`
                        : "Print Selected"}
                    </button>
                  </div>

                  {group.categories.map(({ category, items }) => (
                    <div
                      key={`${group.date}-${category}`}
                      className="rounded-lg border border-slate-100 dark:border-slate-700 overflow-hidden"
                    >
                      <div className="px-3 py-1.5 bg-teal-50 dark:bg-teal-900/20 border-b border-teal-100 dark:border-teal-800/40">
                        <span className="text-xs font-semibold text-teal-700 dark:text-teal-300 uppercase tracking-wide">
                          {category}
                        </span>
                        <span className="ml-2 text-[10px] text-teal-600/80 dark:text-teal-400/80">
                          {items.length} {items.length === 1 ? "drug" : "drugs"}
                        </span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-slate-50/80 dark:bg-slate-700/40">
                            <tr>
                              <th className="px-3 py-1.5 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase w-10">
                                Sel
                              </th>
                              <th className="px-3 py-1.5 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase">
                                Drugs
                              </th>
                              <th className="px-3 py-1.5 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase">
                                Dose
                              </th>
                              <th className="px-3 py-1.5 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase">
                                Frequency
                              </th>
                              <th className="px-3 py-1.5 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase">
                                Duration
                              </th>
                              <th className="px-3 py-1.5 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase">
                                Source
                              </th>
                              <th className="px-3 py-1.5 text-left text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {items.map((item) =>
                              editItem?._id === item._id &&
                              item.source === "current" ? (
                                <PrescriptionForm
                                  key={item._id}
                                  inlineRow
                                  editItem={editItem}
                                  onCancelEdit={() => setEditItem(null)}
                                  onSuccess={() => setEditItem(null)}
                                />
                              ) : (
                              <tr
                                key={item._id}
                                className={`hover:bg-slate-50 dark:hover:bg-slate-700/40 ${
                                  selectedIds.includes(item._id)
                                    ? "bg-teal-50 dark:bg-teal-900/30"
                                    : ""
                                }`}
                              >
                                <td className="px-3 py-2">
                                  <input
                                    type="checkbox"
                                    name={item._id}
                                    checked={selectedIds.includes(item._id)}
                                    onChange={handleCheckboxChange}
                                    className={PRESCRIPTION_CHECKBOX_CLASS}
                                  />
                                </td>
                                <td className="px-3 py-2 text-xs font-medium text-slate-800 dark:text-slate-200">
                                  {item.drugs}
                                </td>
                                <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400">
                                  <span className="px-2 py-0.5 bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 rounded-md text-xs font-medium">
                                    {item.dose}
                                    {item.type}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400">
                                  {item.frequency}
                                </td>
                                <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400">
                                  {item.duration} days
                                </td>
                                <td className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">
                                  {item.source === "current" ? (
                                    <span className="text-teal-600 dark:text-teal-400">
                                      Current
                                    </span>
                                  ) : (
                                    <span className="text-amber-600 dark:text-amber-400">
                                      Past{item.doctorName ? ` · ${item.doctorName}` : ""}
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  {item.source === "current" ? (
                                    <div className="flex items-center gap-1">
                                      <button
                                        type="button"
                                        onClick={() => handleEdit(toEditItem(item))}
                                        className="w-7 h-7 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/50 flex items-center justify-center transition-colors"
                                        aria-label="Edit prescription"
                                      >
                                        <EditIcon sx={{ fontSize: 14 }} />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => remove(item._id)}
                                        className="w-7 h-7 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 flex items-center justify-center transition-colors"
                                        aria-label="Delete prescription"
                                      >
                                        <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                                      </button>
                                    </div>
                                  ) : (
                                    <span className="text-[10px] text-slate-400 dark:text-slate-500">
                                      —
                                    </span>
                                  )}
                                </td>
                              </tr>
                              )
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Prescription Table — hidden while date-wise view is open */}
      {!showDateWise && (
      <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between gap-2">
          <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Prescription List</h4>
          <button
            onClick={() => handlePrint(ID)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-teal-500 to-emerald-600 text-white font-medium text-xs hover:shadow-lg hover:shadow-teal-200 dark:hover:shadow-teal-900/30 transition-all"
          >
            <PrintIcon sx={{ fontSize: 14 }} />
            Print Selected
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-100 dark:border-slate-600">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase w-10">
                  <input
                    type="checkbox"
                    className={PRESCRIPTION_CHECKBOX_CLASS}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedIds(prescription.map((p: any) => p._id));
                      } else {
                        setSelectedIds([]);
                      }
                    }}
                    checked={selectedIds.length === prescription?.length && prescription?.length > 0}
                  />
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Drugs</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Dose</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Frequency</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Duration</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Date</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {prescription?.map((item: any, i: number) => (
                <Fragment key={item._id ?? i}>
                  {editItem?._id === item._id ? (
                    <PrescriptionForm
                      inlineRow
                      editItem={editItem}
                      onCancelEdit={() => setEditItem(null)}
                      onSuccess={() => setEditItem(null)}
                    />
                  ) : (
                    <tr
                      className={`hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${
                        selectedIds.includes(item._id)
                          ? "bg-teal-50 dark:bg-teal-900/30"
                          : ""
                      }`}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          name={item._id}
                          checked={selectedIds.includes(item._id)}
                          onChange={handleCheckboxChange}
                          className={PRESCRIPTION_CHECKBOX_CLASS}
                        />
                      </td>
                      <td className="px-3 py-2 text-xs font-medium text-slate-800 dark:text-slate-200">
                        {item.drugs}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400">
                        <span className="px-2 py-0.5 bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 rounded-md text-xs font-medium">
                          {item.dose}
                          {item.type}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400">
                        {item.frequency}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400">
                        {item.duration} days
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">
                        {item?.createdAt?.split("T")[0]}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleEdit(item)}
                            className="w-7 h-7 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/50 flex items-center justify-center transition-colors"
                            aria-label="Edit prescription"
                          >
                            <EditIcon sx={{ fontSize: 14 }} />
                          </button>
                          <button
                            onClick={() => remove(item?._id)}
                            className="w-7 h-7 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 flex items-center justify-center transition-colors"
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
              {draftFormIds.map((draftId) => (
                <PrescriptionForm
                  key={draftId}
                  inlineRow
                  deferSave
                  draftId={draftId}
                  onDraftChange={handleDraftChange}
                  onDraftRemove={handleDraftRemove}
                />
              ))}
              <tr className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                <td colSpan={7} className="px-3 py-3">
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={addDraftForm}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-dashed border-teal-300 dark:border-teal-600 text-teal-600 dark:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-900/20 text-xs font-medium transition-colors"
                    >
                      <AddCircleOutlineIcon sx={{ fontSize: 18 }} />
                      Add Prescription
                    </button>
                    {draftFormIds.length > 0 && (
                      <button
                        type="button"
                        onClick={saveAllPendingPrescriptions}
                        disabled={isSavingDrafts}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-teal-500 to-emerald-600 text-white font-medium text-xs hover:shadow-lg hover:shadow-teal-200 dark:hover:shadow-teal-900/30 transition-all disabled:opacity-50"
                      >
                        <SaveIcon sx={{ fontSize: 16 }} />
                        {isSavingDrafts
                          ? "Saving..."
                          : `Save Prescriptions (${draftFormIds.length})`}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          {prescription?.length === 0 && draftFormIds.length === 0 && !editItem && (
            <div className="py-8 text-center text-slate-400 dark:text-slate-500">
              <MedicationIcon sx={{ fontSize: 36 }} className="mb-2" />
              <p className="text-xs">No prescriptions added yet</p>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Past Prescriptions */}
      <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
          <HistoryIcon className="text-amber-600 dark:text-amber-400" sx={{ fontSize: 16 }} />
          <div>
            <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Past Prescriptions</h4>
            <p className="text-xs text-slate-500 dark:text-slate-400">{pastPrescriptions.length} records from previous visits</p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-100 dark:border-slate-600">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Visit Date</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Drugs</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Dose</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Frequency</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Duration</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Doctor</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {pastPrescriptions.map((item) => (
                <tr key={item._id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                  <td className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">
                    {(item.visitDate || item.createdAt)?.split("T")[0]}
                  </td>
                  <td className="px-3 py-2 text-xs font-medium text-slate-800 dark:text-slate-200">{item.drugs}</td>
                  <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400">
                    <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 rounded-md text-xs font-medium">
                      {item.dose}{item.type}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400">{item.frequency}</td>
                  <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400">{item.duration} days</td>
                  <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400">{item.doctorName || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {pastPrescriptions.length === 0 && (
            <div className="py-8 text-center text-slate-400 dark:text-slate-500">
              <HistoryIcon sx={{ fontSize: 36 }} className="mb-2" />
              <p className="text-xs">No past prescriptions found</p>
            </div>
          )}
        </div>
      </div>

      {/* Hidden Print Area */}
      <div style={{ position: "absolute", left: "-9999px" }}>
        <div ref={printRef} style={{ display: "none" }}>
          <PrePrint />
        </div>
      </div>
    </div>
  );
}

export default Prescription;
